import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useMarket } from '../../context/MarketContext';
import { formatMarketPrice, getEstimatedDelivery, generateOrderId } from '../../utils/formatPrice';
import { isValidEmail, getMarketAddressConfig } from '../../utils/validators';
import { typeIntoBox, pasteIntoBoxes } from '../../utils/otp';
import env from '../../config/env';
import * as checkoutApi from '../../api/real/checkoutApi';
import * as realOrdersApi from '../../api/real/ordersApi';
import * as paymentApi from '../../api/real/paymentApi';
import { getAttribution } from '../../utils/urlParams';
import { getAvailableCoupons } from '../../api/real/couponsApi';
import { calculateLoyaltyDiscount } from '../../api/real/loyaltyApi';
import { getShippingConfig } from '../../services/shippingConfigService';
import { trackBeginCheckout } from '../../utils/analytics';
import { fireBeginCheckoutConversion } from '../../utils/googleAds';

// ── Guest Checkout Session (24 hours) ─────────────────────────────────────────
// Persisted to localStorage so the guest doesn't re-verify within 24h,
// even if they close the browser tab and return.
const SESSION_KEY = 'skt_checkout_session';
const SESSION_TTL = 24 * 60 * 60 * 1000; // 24 hours

const getSession = (emailHint) => {
  try {
    const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    const expired = Date.now() > session.expiresAt;
    const emailMatch = emailHint ? session.email === emailHint : true;
    if (!expired && emailMatch) return session;
    // Expired — clean up
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  } catch { return null; }
};

const saveSession = (email, token) => {
  const session = { email, token, ts: Date.now(), expiresAt: Date.now() + SESSION_TTL };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
};


// ── Pincode / ZIP Lookup ──────────────────────────────────────────────────────
// India: api.postalpincode.in (returns District + State)
// US and other markets: api.zippopotam.us (free, no key, 60+ countries)
const ZIPPOPOTAM_COUNTRY = { US: 'us', GB: 'gb', AE: 'ae', CA: 'ca', AU: 'au' };

const lookupPincode = async (pin, marketCode = 'IN') => {
  try {
    if (marketCode === 'IN') {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (data[0]?.Status === 'Success' && data[0].PostOffice?.length > 0) {
        const po = data[0].PostOffice[0];
        return { city: po.District, state: po.State, valid: true };
      }
      return { valid: false };
    }

    const countryCode = ZIPPOPOTAM_COUNTRY[marketCode?.toUpperCase()] ?? 'us';
    const res = await fetch(`https://api.zippopotam.us/${countryCode}/${pin}`);
    if (!res.ok) return { valid: false };
    const data = await res.json();
    const place = data.places?.[0];
    if (!place) return { valid: false };
    return {
      city:  place['place name'],
      state: place['state'],
      valid: true,
    };
  } catch { return null; }
};

// ── Shared Components ─────────────────────────────────────────────────────────
const Spinner = ({ sm }) => (
  <svg className={`animate-spin ${sm ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const STEPS = ['Verify', 'Delivery', 'Payment'];
const ProgressBar = ({ step }) => (
  <div className="flex items-center px-5 py-3 border-b border-gray-100 shrink-0">
    {STEPS.map((label, i) => (
      <div key={label} className="flex items-center flex-1 last:flex-none">
        <div className="flex items-center gap-1.5">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all
            ${i < step ? 'bg-emerald-500 text-white' : i === step ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
            {i < step
              ? <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
              : i + 1}
          </div>
          <span className={`hidden sm:block text-xs font-medium whitespace-nowrap
            ${i === step ? 'text-primary' : i < step ? 'text-emerald-600' : 'text-gray-400'}`}>{label}</span>
        </div>
        {i < STEPS.length - 1 && (
          <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-emerald-400' : 'bg-gray-200'}`} />
        )}
      </div>
    ))}
  </div>
);

// ── Step 0: Verify Email ───────────────────────────────────────────────────────
const VerifyStep = ({ onVerified, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail || '');
  const [otpSent, setOtpSent] = useState(false);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setTimeout(() => setResendTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  // Auto-submit on 6 digits filled
  useEffect(() => {
    if (digits.every(d => d !== '') && !verifyingRef.current) {
      doVerify(digits.join(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  const sendOtp = async () => {
    if (!isValidEmail(email)) { toast.error('Enter a valid email address'); return; }
    setLoading(true);
    try {
      if (env.isMock) {
        await new Promise(r => setTimeout(r, 700));
        toast.success(`OTP sent to ${email}! (Demo: 123456)`);
      } else {
        await checkoutApi.sendCheckoutOtp(email);
        toast.success(`OTP sent to ${email}`);
      }
      setOtpSent(true);
      setDigits(['', '', '', '', '', '']);
      setResendTimer(30);
      setTimeout(() => refs[0].current?.focus(), 120);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP. Try again.');
    } finally { setLoading(false); }
  };

  const doVerify = async (code) => {
    if (code.length < 6 || verifyingRef.current) return;
    verifyingRef.current = true;
    setLoading(true);
    try {
      let token;
      if (env.isMock) {
        await new Promise(r => setTimeout(r, 600));
        if (code !== '123456') {
          toast.error('Invalid OTP. Demo code: 123456');
          setDigits(['', '', '', '', '', '']);
          setTimeout(() => refs[0].current?.focus(), 60);
          return;
        }
        token = `mock_token_${Date.now()}`;
      } else {
        const res = await checkoutApi.verifyCheckoutOtp(email, code);
        token = res.checkoutToken;
      }
      saveSession(email, token);
      sessionStorage.setItem('checkout_token', token);
      onVerified(email);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP. Please try again.');
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => refs[0].current?.focus(), 60);
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  };

  // a pasted or phone-filled code fills all six boxes (the effect above then verifies it)
  const showBoxes = (result) => {
    if (!result) return;
    setDigits(result.digits);
    refs[result.focus].current?.focus();
  };

  const handleDigitChange = (i, e) => showBoxes(typeIntoBox(digits, i, e.target.value));

  const handleDigitPaste = (i, e) => {
    e.preventDefault();
    showBoxes(pasteIntoBoxes(digits, i, e.clipboardData.getData('text')));
  };

  const handleDigitKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  return (
    <div className="px-6 py-8 space-y-5">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-primary-light flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-ink">Verify your email</h2>
        <p className="text-sm text-ink-soft mt-1">No sign-up needed — just verify to track your order</p>
      </div>

      {!otpSent ? (
        <div className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendOtp()}
            placeholder="yourname@example.com"
            className="input-field w-full text-center"
            autoFocus
          />
          <button onClick={sendOtp} disabled={loading}
            className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Spinner />}
            {loading ? 'Sending…' : 'Send OTP →'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-sm text-ink-soft flex-wrap">
            <span>Sending to <strong className="text-ink">{email}</strong></span>
            <button onClick={() => { setOtpSent(false); setDigits(['', '', '', '', '', '']); }}
              className="text-primary text-xs font-medium hover:underline">Edit</button>
          </div>
          {env.isMock && (
            <p className="text-center text-xs bg-amber-50 border border-amber-200 rounded-lg py-1.5 text-amber-700">
              Demo OTP: <strong>123456</strong>
            </p>
          )}
          <div className="flex justify-center gap-2">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={refs[i]}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${i + 1} of 6`}
                value={d}
                onChange={e => handleDigitChange(i, e)}
                onPaste={e => handleDigitPaste(i, e)}
                onKeyDown={e => handleDigitKey(i, e)}
                onFocus={e => e.target.select()}
                className="w-11 h-14 text-center text-xl font-bold border border-border rounded-xl
                           focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none
                           bg-surface transition-all disabled:opacity-50"
                disabled={loading}
              />
            ))}
          </div>
          {loading && (
            <p className="text-center text-xs text-ink-muted flex items-center justify-center gap-1.5">
              <Spinner sm /> Verifying…
            </p>
          )}
          <div className="text-center">
            {resendTimer > 0
              ? <p className="text-xs text-ink-muted">Resend OTP in {resendTimer}s</p>
              : <button onClick={sendOtp} disabled={loading} className="text-xs text-primary font-medium hover:underline">
                  Resend OTP
                </button>
            }
          </div>
        </div>
      )}
    </div>
  );
};

// ── Step 1: Delivery ──────────────────────────────────────────────────────────
const DeliveryStep = ({ onNext, onBack, initialAddress, savedAddresses }) => {
  const { currentMarket } = useMarket();
  const marketCode = currentMarket?.isDefault ? 'IN' : (currentMarket?.code ?? 'IN');
  const cfg = getMarketAddressConfig(marketCode);

  // Earlier addresses that are valid for this market (another country's address would be refused at order time).
  const usable = (savedAddresses || []).filter(a =>
    (a.country || cfg.country).trim().toLowerCase() === cfg.country.toLowerCase()
    && cfg.postalPattern.test(a.pincode || ''));
  const toForm = (a) => ({
    name: a.name || '',
    phone: (a.phone || '').replace(/\D/g, '').slice(-cfg.phoneMaxLength),
    pincode: a.pincode || '',
    line1: a.addressLine1 || '',
    line2: a.addressLine2 || '',
    city: a.city || '',
    state: a.state || '',
    country: cfg.country,
  });
  // Typed or chosen by the shopper — from then on, a late-arriving saved list must not overwrite the form.
  const edited = useRef(!!initialAddress?.line1);

  const [addr, setAddr] = useState(() => ({
    name: initialAddress?.name ?? '',
    phone: initialAddress?.phone ?? '',
    pincode: initialAddress?.pincode ?? '',
    line1: initialAddress?.line1 ?? '',
    line2: initialAddress?.line2 ?? '',
    city: initialAddress?.city ?? '',
    state: initialAddress?.state ?? '',
    country: initialAddress?.country ?? cfg.country,
  }));
  const [errors, setErrors] = useState({});
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showLine2, setShowLine2] = useState(!!(initialAddress?.line2));

  // When market changes mid-session, reset postal/city/state and update country
  const prevMarketCode = useRef(marketCode);
  useEffect(() => {
    if (prevMarketCode.current === marketCode) return;
    prevMarketCode.current = marketCode;
    setAddr(a => ({ ...a, pincode: '', city: '', state: '', country: cfg.country }));
    setDeliveryInfo(null);
    setErrors({});
  }, [marketCode, cfg.country]);

  const set = (k, v) => { edited.current = true; setAddr(a => ({ ...a, [k]: v })); };
  const clearErr = (k) => setErrors(e => ({ ...e, [k]: undefined }));

  const applySaved = (a) => {
    setAddr(toForm(a));
    setErrors({});
    setShowLine2(!!a.addressLine2);
    setDeliveryInfo({
      date: getEstimatedDelivery('Prepaid', currentMarket),
      codAvailable: currentMarket?.codEnabled ?? currentMarket?.isDefault ?? true,
    });
  };
  const chooseSaved = (a) => { edited.current = true; applySaved(a); };
  const startNewAddress = () => {
    edited.current = true;
    setAddr({ name: '', phone: '', pincode: '', line1: '', line2: '', city: '', state: '', country: cfg.country });
    setErrors({});
    setShowLine2(false);
    setDeliveryInfo(null);
  };

  // Fill in the most recent address once the list arrives, unless the shopper already started on the form.
  useEffect(() => {
    if (!edited.current && usable.length > 0) applySaved(usable[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses]);

  const sameAs = (a) => {
    const f = toForm(a);
    return ['name', 'phone', 'pincode', 'line1', 'line2'].every(k =>
      (f[k] || '').trim().toLowerCase() === (addr[k] || '').trim().toLowerCase());
  };

  const handlePincodeBlur = async () => {
    if (!cfg.hasPostalLookup || !cfg.postalPattern.test(addr.pincode)) return;
    setPincodeLoading(true);
    try {
      const result = await lookupPincode(addr.pincode, marketCode);
      if (result?.valid) {
        set('city', result.city);
        set('state', result.state);
        clearErr('city');
        clearErr('state');
        clearErr('pincode');
        setDeliveryInfo({
          date: getEstimatedDelivery('Prepaid', currentMarket),
          codAvailable: currentMarket?.codEnabled ?? currentMarket?.isDefault ?? true,
        });
      } else if (result) {
        setErrors(e => ({ ...e, pincode: 'Pincode not found. Please enter city and state manually.' }));
        setDeliveryInfo(null);
      }
    } catch { /* ignore */ }
    finally { setPincodeLoading(false); }
  };

  const validate = () => {
    const e = {};
    if (!addr.name.trim() || addr.name.trim().length < 2) e.name = 'Enter your full name (min 2 chars)';
    if (!cfg.phonePattern.test(addr.phone)) e.phone = cfg.phoneError;
    if (!cfg.postalPattern.test(addr.pincode)) e.pincode = cfg.postalError;
    if (!addr.line1.trim()) e.line1 = 'Address line 1 is required';
    if (!addr.city.trim()) e.city = 'City is required';
    if (!addr.state.trim()) e.state = cfg.stateLabel + ' is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => { onNext({ ...addr, country: cfg.country }); setSubmitting(false); }, 150);
  };

  const inputCls = (k) =>
    `input-field w-full ${errors[k] ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`;

  return (
    <div className="flex flex-col">
      <div className="px-5 py-4 space-y-3 overflow-y-auto">
        <h2 className="text-sm font-bold text-ink">Delivery Address</h2>

        {/* Saved addresses — one tap to reuse an earlier delivery address */}
        {usable.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-ink-soft">Saved addresses</p>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {usable.map((a, i) => {
                const active = sameAs(a);
                return (
                  <button key={i} type="button" onClick={() => chooseSaved(a)}
                    className={`w-full text-left rounded-xl border px-3 py-2 transition-colors ${
                      active ? 'border-primary bg-primary-50' : 'border-border hover:border-primary/50'}`}>
                    <div className="flex items-start gap-2">
                      <span className={`mt-1 w-3 h-3 rounded-full border-2 shrink-0 ${active ? 'border-primary bg-primary' : 'border-gray-300'}`} />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-ink truncate">{a.name} · {a.phone}</p>
                        <p className="text-xs text-ink-soft truncate">
                          {[a.addressLine1, a.addressLine2, a.city, a.state].filter(Boolean).join(', ')} — {a.pincode}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={startNewAddress} className="text-xs text-primary font-medium hover:underline">
              + Use a new address
            </button>
          </div>
        )}

        {/* Name + Phone on same row */}
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">Full Name *</label>
            <input value={addr.name} onChange={e => { set('name', e.target.value); clearErr('name'); }}
              className={inputCls('name')} placeholder="Full name" autoFocus />
            {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">Phone *</label>
            <div className="flex">
              <span className="inline-flex items-center px-2 bg-surface border border-border border-r-0 rounded-l-xl text-xs text-ink-soft select-none whitespace-nowrap">
                {cfg.dialCode}
              </span>
              <input
                value={addr.phone}
                onChange={e => { set('phone', e.target.value.replace(/\D/g, '').slice(0, cfg.phoneMaxLength)); clearErr('phone'); }}
                className={`${inputCls('phone')} rounded-l-none`}
                placeholder={cfg.phoneMaxLength + '-digit'}
                maxLength={cfg.phoneMaxLength}
                inputMode="numeric"
              />
            </div>
            {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
          </div>
        </div>

        {/* Pincode + Country on same row */}
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">{cfg.postalLabel} *</label>
            <div className="relative">
              <input
                value={addr.pincode}
                onChange={e => {
                  const val = cfg.postalDigitsOnly
                    ? e.target.value.replace(/\D/g, '').slice(0, cfg.postalMaxLength)
                    : e.target.value.slice(0, cfg.postalMaxLength);
                  set('pincode', val);
                  clearErr('pincode');
                  setDeliveryInfo(null);
                }}
                onBlur={handlePincodeBlur}
                className={inputCls('pincode')}
                placeholder={cfg.postalPlaceholder}
                maxLength={cfg.postalMaxLength}
                inputMode="numeric"
              />
              {pincodeLoading && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted"><Spinner sm /></span>
              )}
            </div>
            {errors.pincode && <p className="text-red-500 text-xs mt-0.5">{errors.pincode}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">Country</label>
            <input value={cfg.country} readOnly
              className="input-field w-full bg-surface text-ink-soft cursor-not-allowed text-sm" />
          </div>
        </div>
        {deliveryInfo && (
          <p className="text-xs text-emerald-600 font-medium -mt-1">📦 Est. delivery by {deliveryInfo.date}{deliveryInfo.codAvailable ? ' · ✅ COD available' : ''}</p>
        )}

        {/* Address Line 1 */}
        <div>
          <label className="block text-xs font-semibold text-ink-soft mb-1">Address Line 1 *</label>
          <input value={addr.line1} onChange={e => { set('line1', e.target.value); clearErr('line1'); }}
            className={inputCls('line1')} placeholder="House/Flat No., Street Name" />
          {errors.line1 && <p className="text-red-500 text-xs mt-0.5">{errors.line1}</p>}
        </div>

        {/* Address Line 2 — collapsible */}
        {showLine2 ? (
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">
              Address Line 2 <span className="font-normal text-ink-muted">(optional)</span>
            </label>
            <input value={addr.line2} onChange={e => set('line2', e.target.value)}
              className="input-field w-full" placeholder="Area, Landmark, Apt…" autoFocus />
          </div>
        ) : (
          <button type="button" onClick={() => setShowLine2(true)}
            className="text-xs text-primary font-medium hover:underline -mt-1">
            + Add apartment, suite, landmark
          </button>
        )}

        {/* City + State */}
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">City *</label>
            <input value={addr.city} onChange={e => { set('city', e.target.value); clearErr('city'); }}
              className={inputCls('city')} placeholder="City" />
            {errors.city && <p className="text-red-500 text-xs mt-0.5">{errors.city}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-soft mb-1">{cfg.stateLabel} *</label>
            <select value={addr.state} onChange={e => { set('state', e.target.value); clearErr('state'); }}
              className={`${inputCls('state')} bg-white`}>
              <option value="">Select</option>
              {cfg.states.map(s => <option key={s} value={s}>{s}</option>)}
              {addr.state && !cfg.states.includes(addr.state) && <option value={addr.state}>{addr.state}</option>}
            </select>
            {errors.state && <p className="text-red-500 text-xs mt-0.5">{errors.state}</p>}
          </div>
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="px-5 pb-5 pt-3 border-t border-gray-100 shrink-0 bg-white">
        <div className="flex gap-3">
          <button onClick={onBack} className="btn-outline px-4 py-2.5 text-sm">Back</button>
          <button onClick={handleSubmit} disabled={submitting}
            className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting && <Spinner />}
            Continue to Payment →
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Step 2: Payment ───────────────────────────────────────────────────────────
const PaymentStep = ({ onBack, address, email, onOrderComplete }) => {
  const {
    items, itemCount, subtotal, clearCart,
    updateQuantity,
    appliedCoupon, couponDiscount, couponError, couponLoading,
    applyCoupon: ctxApplyCoupon, removeCoupon: ctxRemoveCoupon,
    offer, offerDiscount, setOfferContext, buildCouponItems,
  } = useCart();
  const { user } = useAuth();
  const { currentMarket } = useMarket();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState('Prepaid');
  const [couponInput, setCouponInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCODConfirm, setShowCODConfirm] = useState(false);
  const [cancelledOrder, setCancelledOrder] = useState(null);
  const [couponRemovedMsg, setCouponRemovedMsg] = useState('');
  // Tracks the last coupon removed due to payment mode mismatch — used to show re-apply nudge.
  const [removedCouponForNudge, setRemovedCouponForNudge] = useState(null);
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  // Available coupon browser
  const [showCouponBrowser, setShowCouponBrowser] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState({ applicable: [], almostApplicable: [] });
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  // Global fallback shipping values (from market object or store settings for default market)
  const [globalShippingThreshold, setGlobalShippingThreshold] = useState(
    () => currentMarket?.freeShippingThreshold != null ? Number(currentMarket.freeShippingThreshold) : 699
  );
  const [globalShippingFee, setGlobalShippingFee] = useState(
    () => currentMarket?.shippingBaseFee != null ? Number(currentMarket.shippingBaseFee) : 80
  );
  // Payment options fetched from DB — null while loading (show all as fallback)
  const [paymentOptions, setPaymentOptions] = useState(null);

  // Loyalty points
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);   // { pointsAvailable, pointsApplicable, discount, rupeesPerPoint, earnRate, maxEarnPerOrder }
  const [loyaltyApplied, setLoyaltyApplied] = useState(false);
  const loyaltyDiscount = loyaltyApplied && loyaltyInfo ? Number(loyaltyInfo.discount || 0) : 0;
  const loyaltyPointsToRedeem = loyaltyApplied && loyaltyInfo ? (loyaltyInfo.pointsApplicable || 0) : 0;


  // Load global shipping rules + enabled payment gateways from backend
  useEffect(() => {
    // Always use the fresh market object as source of truth — covers all markets including default.
    // Admin changes in Per-Market Shipping are saved to the market entity and returned via /markets,
    // so reading currentMarket directly is both simpler and correct for all users (no auth needed).
    if (currentMarket?.freeShippingThreshold != null) {
      setGlobalShippingThreshold(Number(currentMarket.freeShippingThreshold));
    }
    if (currentMarket?.shippingBaseFee != null) {
      setGlobalShippingFee(Number(currentMarket.shippingBaseFee));
    }
    if (currentMarket?.shippingBaseFee == null || currentMarket?.freeShippingThreshold == null) {
      // No market-level override: the backend prices the order with the store settings, so show those.
      // (Previously this called the admin-only /admin/settings, which always failed for customers
      // and left the hardcoded 80 / 699 on screen even after the admin changed them.)
      getShippingConfig().then((cfg) => {
        if (!cfg) return;
        if (currentMarket?.shippingBaseFee == null && Number.isFinite(cfg.codFee))
          setGlobalShippingFee(cfg.codFee);
        if (currentMarket?.freeShippingThreshold == null && Number.isFinite(cfg.freeShippingThreshold))
          setGlobalShippingThreshold(cfg.freeShippingThreshold);
      });
    }
    const mktCode = currentMarket?.isDefault ? null : (currentMarket?.code ?? null);
    paymentApi.getPaymentOptions(mktCode)
      .then(options => {
        setPaymentOptions(options || []);
        const hasOnline = (options || []).some(p => p.providerName !== 'COD');
        const hasCOD   = (options || []).some(p => p.providerName === 'COD');
        if (!hasOnline && hasCOD) setPaymentMethod('COD');
        else if (hasOnline) setPaymentMethod('Prepaid');
      })
      .catch(() => { /* keep defaults — show both options */ });
  }, [currentMarket]);

  // The cart's offer is re-judged for this payment method and e-mail (a prepaid-only offer must not show for COD)
  useEffect(() => {
    setOfferContext({ paymentMode: paymentMethod === 'Prepaid' ? 'PREPAID' : 'COD', customerEmail: email || null });
  }, [paymentMethod, email, setOfferContext]);
  useEffect(() => () => setOfferContext({ paymentMode: null, customerEmail: null }), [setOfferContext]);

  // Fetch loyalty config (public endpoint) + points for logged-in customers
  useEffect(() => {
    calculateLoyaltyDiscount(Math.max(0, subtotal - offerDiscount))
      .then(data => {
        setLoyaltyInfo(data);
        // Auto-reset if no points applicable
        if (!data?.pointsApplicable) setLoyaltyApplied(false);
      })
      .catch(() => { /* non-fatal — loyalty section stays hidden */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, subtotal, offerDiscount]);

  // Per-payment-method resolved values — override global fallbacks when market has specific config
  const prepaidFee = currentMarket?.prepaidShippingFee != null
    ? Number(currentMarket.prepaidShippingFee) : globalShippingFee;
  const prepaidThreshold = currentMarket?.prepaidFreeShippingThreshold != null
    ? Number(currentMarket.prepaidFreeShippingThreshold) : globalShippingThreshold;
  const codFee = currentMarket?.codShippingFee != null
    ? Number(currentMarket.codShippingFee) : globalShippingFee;
  // null = COD never qualifies for free shipping unless explicitly configured
  const codThreshold = currentMarket?.codFreeShippingThreshold != null
    ? Number(currentMarket.codFreeShippingThreshold) : null;

  // Active shipping values based on selected payment method
  const shippingThreshold = paymentMethod === 'Prepaid' ? prepaidThreshold : (codThreshold ?? Infinity);
  const shippingFee = paymentMethod === 'Prepaid' ? prepaidFee : codFee;
  // like the server: with a Buy X Get Y offer, shipping and the payment discount use the amount after the offer
  const pricedSubtotal = Math.max(0, subtotal - offerDiscount);
  const shipping = pricedSubtotal >= shippingThreshold ? 0 : shippingFee;
  const discount = couponDiscount;

  // Calculate payment method discount based on current market config
  const paymentMethodDiscount = (() => {
    if (!currentMarket) return 0;
    const isCod = paymentMethod === 'COD';
    const percent = isCod ? currentMarket.codDiscountPercent : currentMarket.prepaidDiscountPercent;
    const flat    = isCod ? currentMarket.codDiscountFlat    : currentMarket.prepaidDiscountFlat;
    const applyMax = isCod ? currentMarket.codApplyMaximum   : currentMarket.prepaidApplyMaximum;
    const minOV   = isCod ? currentMarket.codMinOrderValue   : currentMarket.prepaidMinOrderValue;
    const maxOV   = isCod ? currentMarket.codMaxOrderValue   : currentMarket.prepaidMaxOrderValue;
    if ((!percent || percent === 0) && (!flat || flat === 0)) return 0;
    if (minOV != null && pricedSubtotal < Number(minOV)) return 0;
    if (maxOV != null && pricedSubtotal > Number(maxOV)) return 0;
    const pctDiscount = percent ? Math.round((pricedSubtotal * Number(percent) / 100) * 100) / 100 : 0;
    if (!flat || Number(flat) === 0) return Math.max(0, pctDiscount);
    const flatNum = Number(flat);
    return Math.max(0, applyMax ? Math.max(pctDiscount, flatNum) : Math.min(pctDiscount, flatNum));
  })();

  const total = Math.max(0, subtotal - offerDiscount - discount - paymentMethodDiscount - loyaltyDiscount + shipping);

  // Earn preview: must be after `total` is defined
  const earnPreview = (() => {
    if (!loyaltyInfo?.earnEnabled || !loyaltyInfo?.enabled) return 0;
    const earnRate = Number(loyaltyInfo.earnRate || 1);
    const maxEarn  = Number(loyaltyInfo.maxEarnPerOrder || 500);
    // Earn on cash-paid portion only (exclude what was paid via points)
    const earnableTotal = Math.max(0, total - loyaltyDiscount);
    const raw = Math.floor(earnableTotal / 10) * earnRate;
    return Math.min(raw, maxEarn);
  })();

  // Market-aware price formatter — use throughout this component instead of formatPrice()
  const fmt = (amount) => formatMarketPrice(amount, currentMarket);

  // Auto-switch payment mode when coupon requires it
  useEffect(() => {
    if (!appliedCoupon) return;
    if (appliedCoupon.requiredPaymentMode === 'PREPAID' && paymentMethod !== 'Prepaid') {
      setPaymentMethod('Prepaid');
    } else if (appliedCoupon.requiredPaymentMode === 'COD' && paymentMethod !== 'COD') {
      setPaymentMethod('COD');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCoupon]);

  // Remove coupon when payment mode no longer matches — and store nudge info.
  useEffect(() => {
    if (!appliedCoupon) return;
    const mode = paymentMethod === 'Prepaid' ? 'PREPAID' : 'COD';
    const req = appliedCoupon.requiredPaymentMode;
    if (req && req !== 'ALL' && req !== mode) {
      const saved = { code: appliedCoupon.code, discount: couponDiscount, requiredMode: req };
      ctxRemoveCoupon();
      setRemovedCouponForNudge(saved);
      const modeLabel = req === 'PREPAID' ? 'online payments' : 'Cash on Delivery';
      setCouponRemovedMsg(`${saved.code} is valid for ${modeLabel} only and has been removed.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  // Coupon browser: fetch applicable and almost-applicable coupons for the current cart + mode.
  const loadAvailableCoupons = useCallback(async () => {
    setLoadingCoupons(true);
    try {
      const data = await getAvailableCoupons({
        cartSubtotal: subtotal,
        cartItemCount: itemCount,
        items: buildCouponItems(),
        paymentMode: paymentMethod === 'Prepaid' ? 'PREPAID' : 'COD',
        customerEmail: email,
        marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
      });
      setAvailableCoupons(data || { applicable: [], almostApplicable: [] });
    } catch { /* silent */ }
    finally { setLoadingCoupons(false); }
  }, [subtotal, itemCount, buildCouponItems, paymentMethod, email, currentMarket]);

  useEffect(() => {
    if (showCouponBrowser) loadAvailableCoupons();
  }, [showCouponBrowser, loadAvailableCoupons]);

  // Also re-fetch when payment mode changes while browser is open.
  useEffect(() => {
    if (showCouponBrowser) loadAvailableCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponRemovedMsg('');
    setRemovedCouponForNudge(null);
    await ctxApplyCoupon(couponInput, {
      paymentMode: paymentMethod === 'Prepaid' ? 'PREPAID' : 'COD',
      customerEmail: email,
      marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
    });
    setCouponInput('');
  };

  const handleRemoveCoupon = () => {
    ctxRemoveCoupon();
    setCouponInput('');
    setCouponRemovedMsg('');
  };

  const buildPayload = (method) => {
    const attribution = getAttribution();
    const getCookie = (name) => document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))?.pop() || null;

    // Logged-in users: JWT in Authorization header is sufficient — no checkoutToken needed.
    // Guest users: checkoutToken from the verified session is required.
    const checkoutToken = user
      ? undefined
      : (sessionStorage.getItem('checkout_token') || getSession()?.token || undefined);
    return {
      ...(checkoutToken ? { checkoutToken } : {}),
      email,
      phone: address.phone,
      shippingAddress: {
        name: address.name,
        phone: address.phone,
        addressLine1: address.line1,
        addressLine2: address.line2 || '',
        city: address.city,
        state: address.state,
        pincode: address.pincode,
        country: address.country || 'India',
      },
      items: items.map(item => ({
        productId: item.id,
        variantId: item.selectedVariant?.id ?? item.variants?.[0]?.id ?? null,
        quantity: item.quantity,
      })),
      couponCode: appliedCoupon?.code ?? null,
      loyaltyPointsToRedeem: loyaltyPointsToRedeem || 0,
      paymentMethod: method,
      marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
      // Attribution fields
      fbclid:      attribution.fbclid      || null,
      fbp:         getCookie("_fbp")       || null,
      fbc:         getCookie("_fbc")       || null,
      gclid:       attribution.gclid       || null,
      utmSource:   attribution.utmSource   || null,
      utmMedium:   attribution.utmMedium   || null,
      utmCampaign: attribution.utmCampaign || null,
      utmContent:  attribution.utmContent  || null,
      utmTerm:     attribution.utmTerm     || null,
    };
  };

  const finishOrder = (order, method) => {
    const itemsSnapshot = [...items];
    clearCart();
    // Do NOT clear the guest checkout session — it stays valid for 24h so the
    // guest can place another order without re-verifying their email.
    // Only clear the sessionStorage checkout_token (not the localStorage 24h session).
    sessionStorage.removeItem('checkout_token');
    setLoading(false);
    // Navigate to the dedicated order success page — pass full data as router state.
    navigate(`/order-success/${order.orderNumber}`, {
      replace: true,
      state: {
        order: {
          ...order,
          paymentMethod: method,
          total,
          email,
          items: itemsSnapshot,
          address,
          loyaltyPointsRedeemed: loyaltyPointsToRedeem || 0,
          loyaltyEarnPreview: earnPreview || 0,
          loyaltyRupeesPerPoint: loyaltyInfo?.rupeesPerPoint || 0,
        },
      },
    });
    onOrderComplete(); // signals CheckoutModal to close itself
  };

  const placeCODOrder = async () => {
    setShowCODConfirm(false);
    setLoading(true);
    if (env.isMock) {
      await new Promise(r => setTimeout(r, 1200));
      const order = { orderNumber: generateOrderId(), totalAmount: total, email };
      finishOrder(order, 'COD');
      return;
    }
    let order;
    try {
      order = await realOrdersApi.createOrder(buildPayload('COD'));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Order failed. Please try again.');
      setLoading(false);
      return;
    }
    // From here the order EXISTS (stock reserved). A failed confirmation must not read as "order failed": the customer
    // would order again and get two orders. Retry once, and if it still fails carry on — the store sees the order anyway.
    try {
      await paymentApi.confirmCOD(order.orderNumber);
    } catch {
      try {
        await new Promise(r => setTimeout(r, 1200));
        await paymentApi.confirmCOD(order.orderNumber);
      } catch {
        toast('Your order is placed. Its confirmation may take a few minutes.', { icon: 'ℹ️' });
      }
    }
    finishOrder(order, 'COD');
  };

  const handlePayNow = async () => {
    if (paymentMethod === 'COD') { setShowCODConfirm(true); return; }

    if (env.isMock) {
      setLoading(true);
      await new Promise(r => setTimeout(r, 1200));
      const order = { orderNumber: generateOrderId(), totalAmount: total, email };
      finishOrder(order, 'Prepaid');
      return;
    }

    // Zero-total: confirm without payment gateway
    if (total === 0) {
      setLoading(true);
      try {
        const order = await realOrdersApi.createOrder(buildPayload('RAZORPAY'));
        finishOrder(order, 'Prepaid');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Order failed.');
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      // Decide whether the saved cancelled order can be reused.
      // It can ONLY be reused if the cart is identical (same variants, same quantities)
      // AND the coupon + payment method haven't changed.
      // Any difference → discard the old order and create a fresh one.
      let order;
      if (cancelledOrder) {
        const currentItemKey = items
          .map(i => `${i.variantId}:${i.quantity}`)
          .sort()
          .join('|');
        const savedItemKey = (cancelledOrder.items || [])
          .map(i => `${i.variantId}:${i.quantity}`)
          .sort()
          .join('|');
        const couponMatch  = (cancelledOrder.couponCode || '') === (appliedCoupon?.code || '');
        // The store releases an unpaid order after 30 minutes; never reuse one that old (paying for it would charge
        // the customer for an order that no longer exists).
        const stillFresh   = cancelledOrder.savedAt && (Date.now() - cancelledOrder.savedAt) < 20 * 60 * 1000;
        const cartUnchanged = currentItemKey === savedItemKey && couponMatch && stillFresh;

        if (cartUnchanged) {
          order = cancelledOrder; // safe to reuse — same cart
        } else {
          // Cart changed — cancel the stale order silently, then create a new one
          setCancelledOrder(null);
          try { await realOrdersApi.cancelOrder(cancelledOrder.orderNumber); } catch { /* best-effort */ }
          order = await realOrdersApi.createOrder(buildPayload('RAZORPAY'));
        }
      } else {
        order = await realOrdersApi.createOrder(buildPayload('RAZORPAY'));
      }

      const onlineProvider = paymentOptions?.find(p => p.providerName !== 'COD')?.providerName ?? null;
      const paymentInit = await paymentApi.initiatePayment(order.orderNumber, Number(order.totalAmount), onlineProvider);
      await paymentApi.loadRazorpayScript();

      paymentApi.openRazorpayModal(
        paymentInit,
        order,
        async ({ razorpayOrderId, razorpayPaymentId, razorpaySignature }) => {
          try {
            const confirmed = await paymentApi.verifyPayment({
              razorpayOrderId, razorpayPaymentId, razorpaySignature,
              orderNumber: order.orderNumber,
            });
            setCancelledOrder(null);
            finishOrder(confirmed, 'Prepaid');
          } catch {
            setLoading(false);
            toast('Payment status unclear. Check your orders to confirm.', { icon: 'ℹ️' });
          }
        },
        () => {
          // User dismissed — keep order alive for retry
          setCancelledOrder({ ...order, savedAt: order.savedAt || Date.now() });
          setLoading(false);
        }
      );
    } catch (err) {
      if (err.response?.data?.errorCode === 'ORDER_EXPIRED') {
        // the saved order timed out on the server: forget it, the next click creates a fresh one
        setCancelledOrder(null);
        toast.error('Your payment session expired. Please click Pay again.');
      } else {
        toast.error(err.response?.data?.message || 'Payment initiation failed. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-3.5 space-y-3">
      {/* Razorpay cancelled notice */}
      {cancelledOrder && (
        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <svg className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="text-xs font-bold text-amber-800">Payment cancelled</p>
            <p className="text-xs text-amber-700">Order #{cancelledOrder.orderNumber} is saved. Click Pay to retry.</p>
          </div>
        </div>
      )}

      {/* ── Order Summary (collapsible) ── */}
      <div className="border border-border rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setOrderSummaryOpen(o => !o)}
          className="w-full flex items-center justify-between px-3.5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span className="text-[11px] font-bold text-ink uppercase tracking-widest">
            Order Summary ({itemCount} item{itemCount !== 1 ? 's' : ''})
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-primary">{fmt(subtotal)}</span>
            <svg
              className={`w-4 h-4 text-ink-soft transition-transform ${orderSummaryOpen ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </button>
        {orderSummaryOpen && (
          <div className="divide-y divide-border">
            {items.map(item => (
              <div key={`${item.id}-${String(item.selectedVariant ?? '')}`} className="flex items-center gap-3 px-3.5 py-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-surface shrink-0 border border-border">
                  <img src={item.images?.[0]} alt={item.title} className="w-full h-full object-contain p-0.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-ink line-clamp-1">{item.title}</p>
                  {item.selectedVariant && item.selectedVariant !== 'default' && (
                    <p className="text-[10px] text-ink-muted">{String(item.selectedVariant)}</p>
                  )}
                  <p className="text-xs font-bold text-primary mt-0.5">{fmt(item.price)}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => updateQuantity(item.id, item.selectedVariant, item.quantity - 1)}
                    className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-ink-soft hover:border-primary hover:text-primary transition-colors text-sm leading-none"
                  >−</button>
                  <span className="text-xs font-semibold text-ink w-5 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.selectedVariant, item.quantity + 1)}
                    className="w-6 h-6 rounded-full border border-border flex items-center justify-center text-ink-soft hover:border-primary hover:text-primary transition-colors text-sm leading-none"
                  >+</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Coupon ── */}
      <div className="bg-gray-50 rounded-xl p-3 space-y-2">
        <h3 className="text-[11px] font-bold text-ink uppercase tracking-widest">Promo Code</h3>
        {couponRemovedMsg && (
          <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
            <svg className="w-3.5 h-3.5 text-amber-500 mt-px shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-xs text-amber-700">{couponRemovedMsg}</p>
          </div>
        )}
        {appliedCoupon ? (
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
            <div>
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide">
                {appliedCoupon.code} — saving {fmt(discount)}
              </p>
              {appliedCoupon.requiredPaymentMode === 'PREPAID' && (
                <p className="text-[10px] text-blue-600 mt-0.5">⚡ Requires online payment</p>
              )}
            </div>
            <button onClick={handleRemoveCoupon} className="text-emerald-600 hover:text-red-500 transition-colors ml-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponInput}
                onChange={e => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                placeholder="Enter promo code"
                className="input-field flex-1 text-sm uppercase placeholder:normal-case"
                disabled={couponLoading}
              />
              <button onClick={handleApplyCoupon} disabled={couponLoading || !couponInput.trim()}
                className="btn-outline px-4 text-sm disabled:opacity-50 shrink-0">
                {couponLoading ? <Spinner sm /> : 'Apply'}
              </button>
            </div>
            {couponError && <p className="text-red-500 text-xs pl-0.5">{couponError}</p>}

            {/* ── Available Coupons Browser ── */}
            <button
              type="button"
              onClick={() => setShowCouponBrowser(o => !o)}
              className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline mt-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
              </svg>
              {showCouponBrowser ? 'Hide coupons' : 'View available coupons'}
              <svg className={`w-3 h-3 transition-transform ${showCouponBrowser ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            {showCouponBrowser && (
              <div className="mt-1.5 max-h-48 overflow-y-auto space-y-1.5 pr-0.5">
                {loadingCoupons ? (
                  <p className="text-[11px] text-ink-muted text-center py-3">Loading coupons…</p>
                ) : (availableCoupons.applicable?.length === 0 && availableCoupons.almostApplicable?.length === 0) ? (
                  <p className="text-[11px] text-ink-muted text-center py-3">No coupons available right now</p>
                ) : (
                  <>
                    {/* Applicable coupons */}
                    {availableCoupons.applicable?.length > 0 && (
                      <div className="space-y-1.5">
                        {availableCoupons.applicable.map(c => (
                          <div key={c.code} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-mono font-bold text-xs text-ink">{c.code}</span>
                                {c.paymentMode && c.paymentMode !== 'ALL' && (
                                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                                    {c.paymentMode === 'PREPAID' ? 'Prepaid only' : 'COD only'}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-emerald-700 mt-0.5">
                                {c.shortDescription || (c.discountAmount ? `Save ${fmt(c.discountAmount)}` : c.description)}
                              </p>
                            </div>
                            <button
                              onClick={async () => {
                                setCouponRemovedMsg('');
                                setRemovedCouponForNudge(null);
                                await ctxApplyCoupon(c.code, {
                                  paymentMode: paymentMethod === 'Prepaid' ? 'PREPAID' : 'COD',
                                  customerEmail: email,
                                  marketCode: currentMarket?.isDefault ? null : (currentMarket?.code ?? null),
                                });
                                setShowCouponBrowser(false);
                              }}
                              className="shrink-0 ml-2 text-[11px] font-bold px-2.5 py-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-white transition-all"
                            >
                              Apply
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Not applicable coupons (dimmed, with reason) */}
                    {availableCoupons.almostApplicable?.length > 0 && (
                      <div className="space-y-1.5">
                        {availableCoupons.almostApplicable.map(c => (
                          <div key={c.code} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 opacity-70">
                            <div className="min-w-0 flex-1">
                              <span className="font-mono font-bold text-xs text-ink-soft">{c.code}</span>
                              <p className="text-[11px] text-ink-muted mt-0.5">{c.nudgeMessage || c.shortDescription}</p>
                            </div>
                            <span className="text-[10px] text-ink-muted ml-2 shrink-0">Not applicable</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Loyalty Points ── */}
      {user && loyaltyInfo && loyaltyInfo.pointsAvailable > 0 && (
        <div className={`rounded-xl p-3 border-2 transition-all ${
          loyaltyApplied
            ? 'border-purple-400 bg-purple-50'
            : 'border-border bg-gray-50'
        }`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                loyaltyApplied ? 'bg-purple-600' : 'bg-purple-100'
              }`}>
                <svg className={`w-4 h-4 ${loyaltyApplied ? 'text-white' : 'text-purple-600'}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-ink">
                  {loyaltyInfo.pointsAvailable} Reward Points
                </p>
                {loyaltyInfo.pointsApplicable > 0 ? (
                  <p className="text-[11px] text-ink-soft">
                    Use {loyaltyInfo.pointsApplicable} pts → save {fmt(loyaltyInfo.discount)}
                  </p>
                ) : (
                  <p className="text-[11px] text-ink-muted">
                    Not enough points for this order
                  </p>
                )}
              </div>
            </div>
            {loyaltyInfo.pointsApplicable > 0 && (
              <button
                type="button"
                onClick={() => setLoyaltyApplied(v => !v)}
                className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${
                  loyaltyApplied
                    ? 'border-purple-400 bg-purple-600 text-white'
                    : 'border-primary text-primary hover:bg-primary hover:text-white'
                }`}
              >
                {loyaltyApplied ? '✓ Applied' : 'Apply'}
              </button>
            )}
          </div>
          {loyaltyApplied && (
            <p className="text-[11px] text-purple-600 font-medium mt-2 pl-10">
              🎉 You're saving {fmt(loyaltyDiscount)} with your reward points!
            </p>
          )}
        </div>
      )}

      {/* ── Earn Preview Banner ── */}
      {earnPreview > 0 && (
        <div className="rounded-xl border border-purple-200 overflow-hidden">
          <div className="flex items-center gap-2 px-3.5 py-2.5"
            style={{ background: 'linear-gradient(135deg, #5B21B6, #7C3AED)' }}>
            <svg className="w-4 h-4 text-yellow-300 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
            </svg>
            <p className="text-xs font-bold text-white">
              {user ? "You'll earn reward points!" : "Login & earn reward points!"}
            </p>
          </div>
          <div className="bg-purple-50 px-3.5 py-2.5 flex items-center justify-between">
            <p className="text-xs text-purple-700">Points from this order</p>
            <div className="text-right">
              <p className="text-sm font-bold text-purple-800">+{earnPreview} pts</p>
              {loyaltyInfo?.rupeesPerPoint > 0 && (
                <p className="text-[11px] text-purple-500">
                  ≈ ₹{(earnPreview * Number(loyaltyInfo.rupeesPerPoint)).toFixed(0)} value
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Price Summary ── */}
      <div className="space-y-1.5 text-sm border border-border rounded-xl p-3">
        <div className="flex justify-between text-ink-soft">
          <span>Subtotal</span>
          <span className="font-medium text-ink">{fmt(subtotal)}</span>
        </div>
        {offerDiscount > 0 && (
          <div className="flex justify-between text-emerald-600">
            <span>Offer · {offer.title}</span>
            <span className="font-medium">−{fmt(offerDiscount)}</span>
          </div>
        )}
        {discount > 0 && (
          <div className="flex justify-between text-emerald-600">
            <span>Promo discount</span>
            <span className="font-medium">−{fmt(discount)}</span>
          </div>
        )}
        {paymentMethodDiscount > 0 && (
          <div className="flex justify-between text-emerald-600">
            <span>{paymentMethod === 'Prepaid' ? 'Prepaid discount' : 'COD discount'}</span>
            <span className="font-medium">−{fmt(paymentMethodDiscount)}</span>
          </div>
        )}
        {loyaltyDiscount > 0 && (
          <div className="flex justify-between text-purple-600">
            <span>⭐ Reward points</span>
            <span className="font-medium">−{fmt(loyaltyDiscount)}</span>
          </div>
        )}
        <div className="flex justify-between text-ink-soft">
          <span>Shipping</span>
          <span className={shipping === 0 ? 'text-emerald-600 font-medium' : 'font-medium text-ink'}>
            {shipping === 0 ? 'FREE' : fmt(shipping)}
          </span>
        </div>
        <div className="flex justify-between font-bold text-sm text-ink border-t border-border pt-1.5">
          <span>Total</span>
          <span className="text-primary">{fmt(total)}</span>
        </div>
        <p className="text-[11px] text-ink-muted">📅 Est. delivery: {getEstimatedDelivery(paymentMethod, currentMarket)}</p>
      </div>

      {/* ── Payment Method ── */}
      <div className="space-y-1.5">
        <h3 className="text-[11px] font-bold text-ink uppercase tracking-widest">Payment Method</h3>
        {[
          { id: 'COD',     label: 'Cash on Delivery', icon: '💵',
            desc: codThreshold != null && pricedSubtotal >= codThreshold
              ? 'FREE shipping · Pay when delivered'
              : `${fmt(codFee)} handling · Pay when delivered`,
            gateway: null,
            enabled: paymentOptions === null || paymentOptions.some(p => p.providerName === 'COD') },
          { id: 'Prepaid', label: 'Pay Online',        icon: '💳',
            desc: (() => {
              const methods = currentMarket?.isDefault ? 'UPI · Cards · Net Banking' : 'Cards · International Payment';
              return pricedSubtotal >= prepaidThreshold
                ? `${methods} · FREE shipping!`
                : `${fmt(prepaidFee)} shipping · ${methods}`;
            })(),
            gateway: paymentOptions?.find(p => p.providerName !== 'COD') ?? null,
            enabled: paymentOptions === null || paymentOptions.some(p => p.providerName !== 'COD') },
        ].filter(pm => pm.enabled).map(pm => (
          <label key={pm.id}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 transition-all cursor-pointer
              ${paymentMethod === pm.id ? 'border-primary bg-primary-light' : 'border-gray-200 hover:border-gray-300'}`}
          >
            <input
              type="radio" name="pm_modal" value={pm.id}
              checked={paymentMethod === pm.id}
              onChange={() => {
                setPaymentMethod(pm.id);
                if (removedCouponForNudge) {
                  const match =
                    (pm.id === 'Prepaid' && removedCouponForNudge.requiredMode === 'PREPAID') ||
                    (pm.id === 'COD' && removedCouponForNudge.requiredMode === 'COD');
                  if (match) { setRemovedCouponForNudge(null); setCouponRemovedMsg(''); }
                }
              }}
              className="accent-primary shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-ink text-sm">{pm.icon} {pm.label}</span>
                {pm.id === 'Prepaid' && pricedSubtotal >= prepaidThreshold && paymentMethod === 'Prepaid' && (
                  <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">FREE SHIP</span>
                )}
                {pm.id === 'Prepaid' && (() => {
                  const pct = currentMarket?.prepaidDiscountPercent;
                  const flat = currentMarket?.prepaidDiscountFlat;
                  const minOV = currentMarket?.prepaidMinOrderValue;
                  if ((!pct && !flat) || (minOV != null && pricedSubtotal < Number(minOV))) return null;
                  const label = pct ? `${pct}% off` : `${fmt(flat)} off`;
                  return <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">SAVE {label}</span>;
                })()}
                {pm.id === 'Prepaid' && appliedCoupon?.requiredPaymentMode === 'PREPAID' && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">REQUIRED</span>
                )}
              </div>
              <p className="text-[11px] text-ink-soft leading-snug">{pm.desc}</p>
              {pm.id === 'Prepaid' && removedCouponForNudge?.requiredMode === 'PREPAID' && paymentMethod !== 'Prepaid' && (
                <p className="text-[11px] text-emerald-600 font-medium">💡 Switch to save {fmt(removedCouponForNudge.discount)}</p>
              )}
              {pm.id === 'COD' && removedCouponForNudge?.requiredMode === 'COD' && paymentMethod !== 'COD' && (
                <p className="text-[11px] text-emerald-600 font-medium">💡 Switch to save {fmt(removedCouponForNudge.discount)}</p>
              )}
            </div>
          </label>
        ))}
      </div>

      {/* ── CTA ── */}
      <div className="flex gap-3">
        <button onClick={onBack} className="btn-outline px-4 py-2.5 text-sm">Back</button>
        <button onClick={handlePayNow} disabled={loading}
          className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {loading
            ? <><Spinner />{paymentMethod === 'Prepaid' ? 'Processing…' : 'Placing Order…'}</>
            : <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {cancelledOrder
                  ? `Retry Payment — ${fmt(total)}`
                  : paymentMethod === 'Prepaid'
                    ? `Pay ${fmt(total)}`
                    : `Place Order — ${fmt(total)}`}
              </>
          }
        </button>
      </div>
      <p className="text-xs text-center text-ink-muted">
        By placing this order you agree to our{' '}
        <a href="/return-policy" target="_blank" rel="noreferrer" className="text-primary hover:underline">Return Policy</a>
      </p>

      {/* ── COD Confirm (portal → always above all stacking contexts) ── */}
      {showCODConfirm && createPortal(
        <>
          <div className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm" onClick={() => setShowCODConfirm(false)} />
          <div className="fixed z-[9999]
                          inset-x-4 bottom-8 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2
                          sm:left-1/2 sm:-translate-x-1/2 sm:w-80
                          bg-white rounded-2xl p-6 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">💵</span>
            </div>
            <h3 className="text-base font-bold text-center mb-1">Confirm Cash on Delivery</h3>
            <p className="text-sm text-ink-soft text-center mb-1">
              Total: <strong className="text-ink">{fmt(total)}</strong>
            </p>
            <p className="text-xs text-ink-muted text-center mb-5">Includes {fmt(shippingFee)} handling fee. Pay when delivered.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowCODConfirm(false)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
              <button onClick={placeCODOrder} disabled={loading}
                className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5">
                {loading && <Spinner />}Confirm
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

// ── Main CheckoutModal ────────────────────────────────────────────────────────
const CheckoutModal = () => {
  const { items, isCheckoutOpen, setIsCheckoutOpen, appliedCoupon } = useCart();
  // "Checkout started" once each time checkout opens with something in the bag: GA4 begin_checkout, and the Google Ads
  // "Begin checkout" step when its label is set (Admin → Google Ads → Shopping steps).
  const checkoutTracked = useRef(false);
  useEffect(() => {
    if (!isCheckoutOpen) { checkoutTracked.current = false; return; }
    if (checkoutTracked.current || !items.length) return;
    checkoutTracked.current = true;
    trackBeginCheckout(items, appliedCoupon?.code);
    const value = items.reduce((sum, i) => sum + (Number(i.price) || 0) * (i.quantity || 1), 0);
    fireBeginCheckoutConversion(value, items[0]?.currencyCode || 'INR');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckoutOpen]);
  const { user, logout } = useAuth();
  const location = useLocation();

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState(null);
  // Earlier delivery addresses (null = not loaded yet). Loaded once the e-mail is proven, dropped when checkout closes.
  const [savedAddresses, setSavedAddresses] = useState(null);

  useEffect(() => {
    if (!isCheckoutOpen || step < 1 || !email || env.isMock || savedAddresses !== null) return;
    let stale = false;
    checkoutApi.getSavedAddresses().then(list => { if (!stale) setSavedAddresses(list); });
    return () => { stale = true; };
  }, [isCheckoutOpen, step, email, savedAddresses]);

  useEffect(() => { setSavedAddresses(null); }, [email]);
  useEffect(() => { if (!isCheckoutOpen) setSavedAddresses(null); }, [isCheckoutOpen]);

  // Bug 5: close checkout when the user navigates to /login or /register,
  // and save their progress so it can be restored after auth.
  useEffect(() => {
    if (!isCheckoutOpen) return;
    const authPaths = ['/login', '/register'];
    if (authPaths.includes(location.pathname)) {
      sessionStorage.setItem('skt_checkout_resume', JSON.stringify({
        step, email, address, ts: Date.now(),
      }));
      setIsCheckoutOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Determine starting step on modal open
  useEffect(() => {
    if (!isCheckoutOpen) return;
    // Restore checkout state saved before navigating to /login or /register.
    try {
      const saved = sessionStorage.getItem('skt_checkout_resume');
      if (saved) {
        const resume = JSON.parse(saved);
        sessionStorage.removeItem('skt_checkout_resume');
        if (Date.now() - resume.ts < 5 * 60 * 1000) { // valid for 5 minutes
          if (resume.address) setAddress(resume.address);
          // email and step are set below (auth-aware logic takes precedence)
        }
      }
    } catch { /* ignore */ }

    // Logged-in user → skip verify
    if (user?.email) {
      setEmail(user.email);
      if (step === 0) setStep(1);
      return;
    }
    // Valid 24-hour guest session → skip verify step
    const session = getSession();
    if (session) {
      setEmail(session.email);
      sessionStorage.setItem('checkout_token', session.token);
      if (step === 0) setStep(1);
      return;
    }
    setStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckoutOpen]);

  // Body scroll lock — compensate scrollbar width to prevent layout shift
  useEffect(() => {
    if (isCheckoutOpen) {
      const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarW}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isCheckoutOpen]);

  // Escape key
  useEffect(() => {
    if (!isCheckoutOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsCheckoutOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isCheckoutOpen, setIsCheckoutOpen]);

  const close = useCallback(() => setIsCheckoutOpen(false), [setIsCheckoutOpen]);

  // Someone else is ordering: sign out of the account (or forget the verified guest e-mail) and ask for an e-mail again.
  const changeAccount = useCallback(async () => {
    if (user) await logout();
    try {
      localStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem('checkout_token');
    } catch { /* storage blocked */ }
    setAddress(null);
    setEmail('');
    setStep(0);
  }, [user, logout]);

  if (!isCheckoutOpen) return null;
  if (items.length === 0) { close(); return null; }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 sm:z-[55] backdrop-blur-sm animate-fade-in"
        onClick={close}
      />

      {/* Panel — bottom sheet on mobile, viewport-centered on desktop */}
      <div className="fixed inset-x-0 bottom-0 z-50 sm:z-[60] flex justify-center sm:inset-0 sm:items-center sm:p-4">
        <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl
                        flex flex-col max-h-[96vh] sm:max-h-[85vh] animate-slide-up sm:animate-fade-in">

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="font-bold text-ink text-sm">Secure Checkout — Skinn Thrive</span>
            </div>
            <button onClick={close}
              className="w-8 h-8 rounded-xl bg-surface hover:bg-gray-100 flex items-center justify-center text-ink-muted hover:text-ink transition-all">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress bar */}
          <ProgressBar step={step} />

          {/* Whose order this is, and a way to use another e-mail / account */}
          {step >= 1 && email && (
            <div className="flex items-center justify-between gap-3 px-5 py-2 border-b border-gray-100 bg-surface text-xs shrink-0">
              <span className="min-w-0 truncate text-ink-soft">
                {user ? 'Signed in as' : 'Ordering as'} <strong className="font-semibold text-ink">{email}</strong>
              </span>
              <button type="button" onClick={changeAccount} className="shrink-0 text-primary font-medium hover:underline">
                {user ? 'Sign out' : 'Change'}
              </button>
            </div>
          )}

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 overscroll-contain">
            {step === 0 ? (
              <VerifyStep
                onVerified={(e) => { setEmail(e); setStep(1); }}
                initialEmail={email}
              />
            ) : step === 1 ? (
              <DeliveryStep
                onNext={(addr) => { setAddress(addr); setStep(2); }}
                onBack={close /* the e-mail is already verified — going back leaves checkout */}
                initialAddress={address}
                savedAddresses={savedAddresses}
              />
            ) : (
              <PaymentStep
                onBack={() => setStep(1)}
                address={address}
                email={email}
                onOrderComplete={() => setIsCheckoutOpen(false)}
              />
            )}
          </div>

          {/* iOS safe area */}
          <div className="h-safe-area-bottom shrink-0" />
        </div>
      </div>
    </>
  );
};

export default CheckoutModal;
